// Global search palette (1047) — Vercel/Linear/Raycast shape. Server-
// side, heterogeneous, RLS-scoped. Replaces the Phase-0 wallets-only
// local-prefix palette (`site-search-wallets.tsx`).
//
// Flow:
//   1. On open with empty query → POST /resources/search {q:'',types:
//      ['wallet','position']} to populate the default slate (~10 most-
//      recently-active per type, RLS-scoped).
//   2. On input → 200ms debounce → POST /resources/search with query.
//   3. On select → navigate via /book/<bookId>?next=<href> when bookId
//      is set (atomic cookie swap, matches scope-dropdown pattern), or
//      directly to href when bookId is null.
//   4. Paste-to-resolve fallback (/resources/resolve-address) handles
//      pasted base58 addresses that aren't in the search index — e.g.
//      external metlex position addresses pointing at a book the user
//      can see but isn't surfaced by name/prefix match.
//
// Anon users see a Sign up/in link instead of the palette (1032
// decision; the data behind this surface is gated).

import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useFetcher, useLocation, useNavigate } from 'react-router'
import { Button, buttonVariants } from '#app/components/ui/button.tsx'
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/components/ui/command.tsx'
import { type EntityType, type SearchResult } from '#app/features/search/search-service.server.ts'
import { type ResolveResult } from '#app/routes/resources+/resolve-address.ts'
import { type SearchResponse } from '#app/routes/resources+/search.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

const BASE58_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
const DEBOUNCE_MS = 200
const DEFAULT_TYPES: EntityType[] = ['wallet', 'position']

const TYPE_HEADINGS: Record<EntityType, string> = {
	wallet: 'Wallets',
	position: 'Positions',
}

export function GlobalSearchPalette() {
	const user = useOptionalUser()
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const searchFetcher = useFetcher<SearchResponse>()
	const resolveFetcher = useFetcher<ResolveResult>()
	const location = useLocation()
	const navigate = useNavigate()
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// cmd+K / ctrl+K global shortcut — authed only. The anon branch
	// returns a sign-up Link below; the keybind would open a dialog
	// behind a mounted-but-unseen component.
	useEffect(() => {
		if (!user) return
		function onKey(e: KeyboardEvent) {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				setOpen((v) => !v)
			}
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [user])

	// Default slate on first open — "your things + public things" per
	// RLS, no explicit branch. Fire once per open; the user can type to
	// override. The dependency on `open` is intentional and surfaced to
	// the linter via an inline-disable to match the existing pattern.
	useEffect(() => {
		if (!user) return
		if (open && !searchFetcher.data && searchFetcher.state === 'idle') {
			postSearch('')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	// Debounced search on query change. Any pending timer is cleared;
	// the latest keystroke wins.
	useEffect(() => {
		if (!user || !open) return
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			postSearch(query)
		}, DEBOUNCE_MS)
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query])

	function postSearch(q: string) {
		searchFetcher.submit(
			JSON.stringify({ q, types: DEFAULT_TYPES, limit: 10 }),
			{
				method: 'post',
				action: '/resources/search',
				encType: 'application/json',
			},
		)
	}

	// Anon: palette surface is retired (1032). Trigger becomes a signup
	// link using the auth-prompt label.
	if (!user) {
		return (
			<Link
				to="/signup"
				className={cn(
					buttonVariants({ variant: 'outline', size: 'sm' }),
					'text-muted-foreground w-full max-w-xl justify-start',
				)}
			>
				<span className="flex items-center gap-2">
					<Search className="h-3.5 w-3.5" />
					<span className="text-xs">Sign up/in to search wallets</span>
				</span>
			</Link>
		)
	}

	const trimmed = query.trim()
	const looksLikeAddress = BASE58_ADDRESS.test(trimmed)
	const results: SearchResult[] = searchFetcher.data?.results ?? []
	const byType = groupByType(results)
	const nothingFound = !searchFetcher.data || results.length === 0

	// Paste-to-resolve fallback surfaces only when the current query is
	// a full base58 address AND search returned nothing matching it.
	// Covers the edge case of a pasted position address that isn't in
	// the index yet.
	const showResolveFallback =
		looksLikeAddress && nothingFound && searchFetcher.state === 'idle'

	function navSelect(r: SearchResult) {
		setOpen(false)
		setQuery('')
		const href = r.href || location.pathname
		if (r.bookId) {
			const next = encodeURIComponent(href)
			navigate(`/book/${r.bookId}?next=${next}`)
		} else {
			navigate(href)
		}
	}

	function submitResolve() {
		if (!looksLikeAddress) return
		const fd = new FormData()
		fd.set('address', trimmed)
		resolveFetcher.submit(fd, {
			method: 'post',
			action: '/resources/resolve-address',
		})
	}

	// Auto-navigate on a single resolver match — same metlex-URL paste
	// shortcut from the previous palette implementation. The resolver
	// returns bookId; swap the cookie via /book/<id>?next=<current> so
	// the user lands on the same page but in the newly-scoped book.
	useEffect(() => {
		if (
			resolveFetcher.state === 'idle' &&
			resolveFetcher.data &&
			resolveFetcher.data.address === trimmed &&
			resolveFetcher.data.matches.length === 1
		) {
			const m = resolveFetcher.data.matches[0]
			if (m) {
				setOpen(false)
				setQuery('')
				const next = encodeURIComponent(location.pathname)
				navigate(`/book/${m.bookId}?next=${next}`)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolveFetcher.state, resolveFetcher.data])

	const resolveServerResult =
		resolveFetcher.data && resolveFetcher.data.address === trimmed
			? resolveFetcher.data
			: null
	const resolveBusy = resolveFetcher.state !== 'idle'

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => setOpen(true)}
				className="text-muted-foreground w-full max-w-xl justify-between"
			>
				<span className="flex items-center gap-2">
					<Search className="h-3.5 w-3.5" />
					<span className="text-xs">Search wallets, positions, or paste an address…</span>
				</span>
				<kbd className="pointer-events-none inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
					<span className="text-xs">⌘</span>K
				</kbd>
			</Button>
			<CommandDialog
				open={open}
				onOpenChange={setOpen}
				className="sm:max-w-[720px]"
				// Server-side filter — cmdk's local filter would re-filter
				// the already-filtered server results and discard valid
				// hits that don't match on the typed value field.
				shouldFilter={false}
			>
				<CommandInput
					placeholder="Search wallets, positions, or paste an address…"
					value={query}
					onValueChange={setQuery}
				/>
				<CommandList className="max-h-[65vh] min-h-[420px]">
					{searchFetcher.state === 'loading' && results.length === 0 ? (
						<div className="text-muted-foreground px-3 py-6 text-center text-xs">
							Searching…
						</div>
					) : null}

					{nothingFound &&
					searchFetcher.state === 'idle' &&
					!showResolveFallback ? (
						<CommandEmpty>
							{trimmed.length > 0
								? 'No matches. Paste a full address to resolve.'
								: 'Start typing a wallet or position, or paste an address.'}
						</CommandEmpty>
					) : null}

					{DEFAULT_TYPES.map((t) => {
						const items = byType[t] ?? []
						if (items.length === 0) return null
						return (
							<CommandGroup key={t} heading={TYPE_HEADINGS[t]}>
								{items.map((r) => (
									<CommandItem
										key={r.id}
										value={r.id}
										onSelect={() => navSelect(r)}
									>
										<div className="flex min-w-0 flex-col">
											<span className="font-medium">{r.label}</span>
											<span className="text-muted-foreground truncate text-[11px]">
												{r.secondary}
											</span>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						)
					})}

					{showResolveFallback && !resolveServerResult ? (
						<CommandItem
							key="__resolve__"
							value={`__resolve__ ${trimmed}`}
							onSelect={submitResolve}
							disabled={resolveBusy}
						>
							<div className="flex w-full items-center justify-between gap-4">
								<span>
									{resolveBusy
										? 'Resolving…'
										: `Resolve address ${trimmed.slice(0, 6)}…${trimmed.slice(-4)}`}
								</span>
								<span className="text-muted-foreground text-[10px]">
									server lookup
								</span>
							</div>
						</CommandItem>
					) : null}

					{resolveServerResult && resolveServerResult.matches.length > 1 ? (
						<CommandGroup heading="Resolver matches">
							{resolveServerResult.matches.map((m) => (
								<CommandItem
									key={`__resolved__:${m.via}:${m.bookId}`}
									value={`__resolved__ ${m.via} ${m.bookId} ${m.bookName}`}
									onSelect={() => {
										setOpen(false)
										setQuery('')
										const next = encodeURIComponent(location.pathname)
										navigate(`/book/${m.bookId}?next=${next}`)
									}}
								>
									<div className="flex w-full items-center justify-between gap-4">
										<span className="truncate">{m.bookName}</span>
										<span className="text-muted-foreground font-mono text-[10px]">
											{m.via}
											{m.via === 'wallet' ? ` · ${m.nEvents} events` : ''}
										</span>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					) : null}

					{resolveServerResult && resolveServerResult.matches.length === 0 ? (
						<div className="text-muted-foreground px-3 py-6 text-center text-xs">
							No book contains this address.
						</div>
					) : null}
				</CommandList>
			</CommandDialog>
		</>
	)
}

function groupByType(results: SearchResult[]): Record<EntityType, SearchResult[]> {
	const out: Record<EntityType, SearchResult[]> = { wallet: [], position: [] }
	for (const r of results) {
		if (r.type in out) out[r.type].push(r)
	}
	return out
}
